import { t } from 'i18next'
import { Button } from 'primereact/button'
import { FileUpload, FileUploadHandlerParam } from 'primereact/fileupload'
import { Toast } from 'primereact/toast'
import React, { useEffect, useRef, useState } from 'react'
import {
  API_DOCUMENT_TYPES,
  API_DOSSIER_FILE,
  API_DOSSIER_PERSON,
  MAX_DOSSIER_SIZE,
  MAX_FILE_SIZE,
  MAX_MULTIUPLOAD,
} from '../../../../constants'
import {
  DossierDb,
  MokaiDocumentType,
  UploadMask,
} from '../../../../model/dossier.model'
import { RequestMethod } from '../../../../model/enums/enums'
import { Person } from '../../../../model/person.model'
import { addDocumentType } from '../../../../redux/basic/dossier/documentTypeAction'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import { DocumentUpload } from '../../dossier/DocumentUpload'
import { DossierDataTable } from '../../dossier/DossierDataTable'
import { cutEnding, getDocumentType } from '../../dossier/Helpers'

/** This is a hook component to display the dossier of a person
 * and for uploading files to it
 * it is, in spirit, very similar to AllDossiersList but kept separate
 * in case the two diverge in functionality at a future point
 */
export const PersonDocumentList = (props: {
  // should never be undefined
  person: Person | undefined // the current person
  // only allows undefined due to propagation of the parent element
  toastRef: React.RefObject<Toast> // toast for messages
}): JSX.Element => {
  const [currentDossier, setCurrentDossier] = useState<DossierDb[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [uploadedFilesMask, setUploadedFilesMask] = useState<UploadMask[]>([])
  const [canReload, setCanReload] = useState<boolean>(true)

  const fileUploadRef = useRef<FileUpload>(null)

  const documentTypes = useAppSelector((state) => state.documentTypes)

  const dispatcher = useAppDispatch()

  const fetchFromDB = async () => {
    const personId = props.person?.person_id
    if (!personId) {
      console.warn('Can not fetch. PersonId not defined.')
      return
    }
    let url = `${API_DOSSIER_PERSON(personId)}`
    const dossier = await handleRequest<DossierDb[]>(url, RequestMethod.GET)
    if (documentTypes.length === 0) {
      const docTypes = await handleRequest<MokaiDocumentType[]>(
        API_DOCUMENT_TYPES(),
        RequestMethod.GET
      )
      // sort by alphabet
      docTypes.data?.sort((a, b) =>
        a.document_type_name.localeCompare(b.document_type_name)
      )
      // but move Sonstiges to the back
      const idx = docTypes.data?.findIndex(
        (a) => a.document_type_name === 'Sonstiges'
      )
      if (idx) {
        docTypes.data?.push(docTypes.data?.splice(idx, 1)[0])
      }
      docTypes.data?.forEach((type) => dispatcher(addDocumentType(type)))
    }
    setCurrentDossier(dossier.data ? dossier.data : [])
  }

  // we need to fetch this whenever person is loaded
  useEffect(() => {
    fetchFromDB()
  }, [props.person])

  const onFileUpload = (e: FileUploadHandlerParam) => {
    if (uploadedFiles.length + e.files.length > MAX_MULTIUPLOAD) {
      props.toastRef.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('dossier.files_exceed.title'),
        detail: t<string>('dossier.files_exceed.message', {
          num: MAX_MULTIUPLOAD,
        }),
        life: 4000,
      })
      fileUploadRef.current?.clear()
      return
    }

    if (e.files.filter((f) => f.size === 0).length > 0) {
      props.toastRef.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('dossier.empty_file.title'),
        detail: t<string>('dossier.empty_file.message'),
        life: 4000,
      })
      fileUploadRef.current?.clear()
      return
    }

    const defaultValues = e.files.map((file: File) => {
      return {
        document_name: cutEnding(file.name),
        comment: '',
        type: getDocumentType(file.name, documentTypes),
      }
    })

    let curFiles = [...uploadedFiles]

    curFiles = curFiles.concat([...e.files])

    setUploadedFiles(curFiles)

    // does this have any setbacks?
    // we only want to set the default mask once
    if (uploadedFilesMask.length === 0) setUploadedFilesMask(defaultValues)
    fileUploadRef.current?.clear()
  }

  const closeUploadDialogCb = (changed: boolean) => {
    fileUploadRef.current?.clear()
    setUploadedFiles([])
    setUploadedFilesMask([])

    if (changed) {
      setCanReload(false)
      setTimeOut().then((_) => onClickReload())
    }
  }

  const setTimeOut = async (timeout: number = 3000) => {
    await new Promise((resolve) => setTimeout(resolve, timeout))
  }

  const removeFile = (idx: number) => {
    if (uploadedFiles) {
      const uploadeFilesCopy = [...uploadedFiles]
      const filtered_array = uploadeFilesCopy?.filter((e, i) => i !== idx)
      setUploadedFiles(filtered_array)
    }
  }

  const onClickReload = () => {
    setCanReload(false)
    fetchFromDB()
      .then(async () => {
        await setTimeOut().then((_) => {
          setCanReload(true)
        })
      })
      .catch((err) => console.log(err))
      .finally(() => {
        setCanReload(true)
      })
  }

  const onRowEditComplete = async (e: any): Promise<void> => {
    let currentDossierTmp = [...currentDossier]
    let { newData, index } = e

    if (
      !(
        currentDossier[index].comment === newData.comment &&
        currentDossier[index].id_documentType === newData.id_documentType
      )
    ) {
      currentDossierTmp[index] = newData
      setCurrentDossier(currentDossierTmp)
      const body = {
        comment: newData.comment,
        documentType: newData.id_documentType,
      }
      const result = await handleRequest<void>(
        API_DOSSIER_FILE(currentDossierTmp[index].dossier_file_id),
        RequestMethod.PUT,
        body
      )

      if (!result.error) {
        props.toastRef.current?.show({
          closable: true,
          severity: 'success',
          summary: t<string>('core.action.edit.success.title'),
          detail: t<string>('core.action.edit.success.message'),
          life: 8000,
        })
        return
      }
      props.toastRef.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('core.action.edit.failed.title'),
        detail: t<string>('core.action.edit.failed.message'),
        life: 12000,
      })
    }
  }

  const sizeFooter = (): JSX.Element => {
    let curSize = currentDossier?.reduce((prev, cur) => prev + cur.size, 0)
    if (!curSize) curSize = 0

    return (
      <div className="flex justify-content-between">
        <div className="flex">
          <FileUpload
            ref={fileUploadRef}
            mode="basic"
            chooseLabel={t<string>('core.upload_files')}
            multiple={true}
            maxFileSize={MAX_FILE_SIZE}
            auto={true}
            customUpload={true}
            uploadHandler={onFileUpload}
          ></FileUpload>
          <Button
            className="p-button-text"
            tooltip={t<string>('dossier.reload')}
            icon={'fa-solid fa-arrow-rotate-right'}
            disabled={!canReload}
            loading={!canReload}
            onClick={() => onClickReload()}
          ></Button>
        </div>
        <div className="mt-2">
          {`${(curSize / 1e6).toFixed(2)}MB/${(MAX_DOSSIER_SIZE / 1e6).toFixed(
            2
          )}MB`}
        </div>
      </div>
    )
  }

  return (
    <>
      <DossierDataTable
        scrollHeight={'40vh'}
        showPerson={false}
        currentDossier={currentDossier}
        documentTypes={documentTypes}
        toastRef={props.toastRef}
        sizeFooter={sizeFooter}
        setCurrentDossier={setCurrentDossier}
        onRowEditComplete={onRowEditComplete}
      />
      {uploadedFiles.length > 0 && (
        <DocumentUpload
          removeFile={removeFile}
          currentDossier={currentDossier}
          toastRef={props.toastRef}
          defaultValues={uploadedFilesMask}
          cleanupCallback={closeUploadDialogCb}
          person={props.person}
          files={uploadedFiles}
          showDeleteButton={true}
        />
      )}
    </>
  )
}